import type { Blocker, BlockerFunction } from 'react-router'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useBlocker } from 'react-router'

type NavigationBlockerContextValue = {
  addCondition: (condition: BlockerFunction) => () => void
  blocker: Blocker
}

const unblocked: Blocker = { state: 'unblocked', proceed: undefined, reset: undefined, location: undefined }

const NavigationBlockerContext = createContext<NavigationBlockerContextValue>({
  addCondition: () => () => {},
  blocker: unblocked,
})

type Props = {
  children: ReactNode
  /**
   * Runs when a navigation starts, whether or not a condition blocks it; a
   * blocked navigation that proceeds does not run it again
   */
  onBeforeNavigation?: (args: Parameters<BlockerFunction>[0]) => void
}

/**
 * Registers the router's only blocker: the pinned React Router 7.6 honors only
 * the `useBlocker` registered last (removable once it honors several).
 * Components block navigations through `useBlockNavigation` instead, and a
 * navigation is blocked when any of their conditions holds.
 */
export function NavigationBlockerProvider({ children, onBeforeNavigation }: Readonly<Props>) {
  const conditions = useRef(new Set<BlockerFunction>())

  const blocker = useBlocker((args) => {
    onBeforeNavigation?.(args)
    return [...conditions.current].some((condition) => condition(args))
  })

  const addCondition = useCallback((condition: BlockerFunction) => {
    conditions.current.add(condition)
    return () => {
      conditions.current.delete(condition)
    }
  }, [])

  const value = useMemo(() => ({ addCondition, blocker }), [addCondition, blocker])

  return <NavigationBlockerContext.Provider value={value}>{children}</NavigationBlockerContext.Provider>
}

/**
 * Blocks a navigation while `condition` holds, for as long as the calling
 * component is mounted, and returns the router's blocker to proceed or reset.
 * The blocker is shared: other components' conditions block it too. Outside a
 * `NavigationBlockerProvider` nothing is blocked.
 */
export function useBlockNavigation(condition: BlockerFunction): Blocker {
  const { addCondition, blocker } = useContext(NavigationBlockerContext)
  const latest = useRef(condition)

  useEffect(() => {
    latest.current = condition
  })

  useEffect(() => addCondition((args) => latest.current(args)), [addCondition])

  return blocker
}
